// Cancellation-reason modal — FE-042b.
//
// Opens when a Kanban move targets the Cancelled column. The backend
// rejects ``to_status=cancelled`` without a non-empty
// ``cancellation_reason`` (services.transition_commitment_status), so
// we collect it up-front instead of letting the optimistic move flip
// to a server error mid-flight.

import { useEffect, useRef, useState } from "react";


export interface CancellationReasonModalProps {
  open: boolean;
  /** Title of the card being cancelled — shown so the user can
   * confirm they're cancelling the right thing. */
  title: string;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}


export function CancellationReasonModal({
  open, title, isPending, onCancel, onConfirm,
}: CancellationReasonModalProps) {
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!open) {
      setReason("");
      setTouched(false);
      return;
    }
    // Focus the textarea on open so keyboard users can start typing
    // immediately. ``requestAnimationFrame`` waits for the modal to
    // mount in the DOM.
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [open]);

  if (!open) return null;
  const trimmed = reason.trim();
  const blank = trimmed.length === 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="commitments-cancel-title"
      data-testid="commitments-cancel-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-text-primary/40"
    >
      <div className="w-full max-w-sm rounded-lg border border-border-subtle bg-raised p-4 shadow-lg">
        <h2
          id="commitments-cancel-title"
          className="text-sm font-semibold text-text-primary"
        >
          Cancel "{title}"?
        </h2>
        <p className="mt-1 text-xs text-text-secondary">
          Cancellations are recorded and surfaced in the audit trail.
          Add a brief reason so reviewers can pick up context.
        </p>
        <label
          className="mt-3 flex flex-col gap-1 text-xs font-medium text-text-secondary"
          htmlFor="commitments-cancel-reason"
        >
          Reason
          <textarea
            id="commitments-cancel-reason"
            ref={inputRef}
            data-testid="commitments-cancel-reason"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (!touched) setTouched(true);
            }}
            rows={3}
            className="rounded-md border border-border-subtle bg-raised px-2 py-1 text-sm text-text-primary"
          />
        </label>
        {touched && blank && (
          <p
            data-testid="commitments-cancel-reason-error"
            className="mt-1 text-[11px] font-medium text-ember"
          >
            Reason is required.
          </p>
        )}
        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            data-testid="commitments-cancel-modal-dismiss"
            onClick={onCancel}
            className="rounded-md border border-border-subtle px-3 py-1 text-xs text-text-secondary hover:text-text-primary"
          >
            Keep open
          </button>
          <button
            type="button"
            data-testid="commitments-cancel-modal-submit"
            disabled={blank || isPending}
            onClick={() => {
              setTouched(true);
              if (!blank) onConfirm(trimmed);
            }}
            className="rounded-md bg-ember px-3 py-1 text-xs font-medium text-text-inverse hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isPending ? "Cancelling…" : "Cancel Commitment"}
          </button>
        </div>
      </div>
    </div>
  );
}
